import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Package, Search, Plus, AlertTriangle } from 'lucide-react'
import { geraete as geraeteApi } from '../../lib/api'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { geraetStatusBadge } from '../../components/ui/Badge'
import { EmptyState, LoadingSpinner } from '../../components/ui/EmptyState'
import { formatDatum, garantieStatus } from '../../lib/utils'
import { cn } from '../../lib/utils'

export function InventarListePage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('alle')

  const { data, isLoading } = useQuery({
    queryKey: ['geraete'],
    queryFn: () => geraeteApi.list(),
  })

  const filtered = (data ?? []).filter((g) => {
    const matchSearch = [g.modell, g.seriennummer, g.kunde_name, g.standort].some((v) =>
      v?.toLowerCase().includes(search.toLowerCase())
    )
    const matchStatus = statusFilter === 'alle' || g.status === statusFilter
    return matchSearch && matchStatus
  })

  return (
    <>
      <TopBar
        title="Inventar"
        rightAction={<Button icon={<Plus size={18} />} size="sm" onClick={() => navigate('/inventar/neu')}>Neu</Button>}
      />

      <div className="px-4 py-3 space-y-3">
        <Input placeholder="Suche nach Modell, SN, Kunde..." value={search} onChange={(e) => setSearch(e.target.value)} icon={<Search size={16} />} />

        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          {['alle', 'aktiv', 'defekt', 'ersetzt'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cn('shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-colors',
                statusFilter === s ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
              )}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {isLoading ? <LoadingSpinner /> : filtered.length === 0 ? (
          <EmptyState icon={<Package size={28} />} title="Keine Geräte" description="Gerät hinzufügen" action={<Button icon={<Plus size={16} />} onClick={() => navigate('/inventar/neu')}>Gerät hinzufügen</Button>} />
        ) : (
          <div className="space-y-2">
            {filtered.map((g) => {
              const gs = garantieStatus(g.garantie_bis)
              return (
                <Card key={g.id} clickable onClick={() => navigate(`/inventar/${g.id}`)} className="flex items-center gap-3">
                  <div className={cn('w-12 h-12 rounded-2xl flex items-center justify-center shrink-0',
                    g.status === 'defekt' ? 'bg-red-50' : 'bg-blue-50'
                  )}>
                    {g.status === 'defekt'
                      ? <AlertTriangle size={20} className="text-red-500" />
                      : <Package size={20} className="text-blue-600" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="font-semibold text-sm text-gray-900">{g.modell}</p>
                      {g.abo_aktiv ? <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full">Abo</span> : null}
                    </div>
                    <p className="text-xs text-gray-600 truncate">{g.kunde_name}</p>
                    <div className="flex gap-2 items-center mt-1">
                      {g.seriennummer && <span className="text-xs text-gray-400">SN: {g.seriennummer}</span>}
                      {gs !== 'gueltig' && (
                        <span className={cn('text-xs', gs === 'abgelaufen' ? 'text-red-500' : 'text-amber-500')}>
                          Garantie {gs === 'abgelaufen' ? 'abgelaufen' : 'läuft bald ab'}
                        </span>
                      )}
                    </div>
                  </div>
                  {geraetStatusBadge(g.status)}
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}
